import { useState } from 'react'

import type { useCarrierLegacyUnit } from '@/components/carrier/use-carrier-legacy-unit'

import { legacyUnitOptions } from '@/components/carrier/carrier-legacy-unit-options'
import { LEGACY_UNIT_FIELD_ID } from '@/components/carrier/use-carrier-legacy-unit'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useCarrierLegacySearch } from '@/hooks/use-carrier-shipment-query'
import { useDebounced } from '@/hooks/use-debounced'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

interface Props {
    unit: ReturnType<typeof useCarrierLegacyUnit>
    orderId: string
    /** The receiver's ward and province as currently typed. */
    wardCode: string
    provinceCode?: string
    /** The merged ward the order is addressed to. */
    wardName?: string | null
    /** Moves the receiver to the ward of a commune found by search; absent
     *  when the receiver cannot be edited, which hides other wards. */
    onWardChange?: (ward: { code: string; name: string }) => void
    onInteract?: () => void
}

/**
 * The old commune Viettel Post prints on the label for a merged ward. Green
 * once the system recognised it or the operator chose it; yellow while it is
 * only a guess. The reason stays on screen: operators read it as "type the old
 * address again" when it hid behind a hover icon.
 */
export function CarrierLegacyUnitRow({
    unit,
    orderId,
    wardCode,
    provinceCode,
    wardName,
    onWardChange,
    onInteract,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search)
    const searchQuery = useCarrierLegacySearch(orderId, {
        q: debouncedSearch.trim(),
        ...(provinceCode ? { province_code: provinceCode } : {}),
    })
    const { addressWard } = unit
    // Booking a commune of another ward moves the receiver there.
    const moveTo = (
        ward: { code: string; name: string },
        id: number,
        fromAddressWard = false,
    ) => {
        unit.chooseInWard(ward.code, id, fromAddressWard)
        onWardChange?.(ward)
    }
    // Shown even for a ward without old communes: it can still be wrong.
    const addressWarning = addressWard && (
        <div
            className="border-destructive/40 bg-destructive/5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-sm"
            role="status"
        >
            <span>
                {m.carrier_legacy_address_ward({
                    commune: addressWard.ward_name,
                    district: addressWard.district_name,
                    ward: addressWard.current_ward_name,
                    current: wardName ?? '',
                })}
            </span>
            {onWardChange && (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        onInteract?.()
                        moveTo(
                            {
                                code: addressWard.current_ward_code,
                                name: addressWard.current_ward_name,
                            },
                            addressWard.vtp_ward_id,
                            true,
                        )
                    }}
                >
                    {m.carrier_legacy_address_ward_move({
                        ward: addressWard.current_ward_name,
                    })}
                </Button>
            )}
        </div>
    )
    if (!unit.visible) {
        if (unit.loadFailed) {
            return (
                <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-destructive h-auto justify-start p-0"
                    onClick={unit.retry}
                >
                    {m.carrier_legacy_load_failed()}
                </Button>
            )
        }
        if (addressWarning) return addressWarning
        return unit.error ? (
            <p className="text-destructive text-sm" role="alert">
                {unit.error}
            </p>
        ) : null
    }
    const { candidates, others, confirmed, error } = unit
    const { options, moveFor } = legacyUnitOptions({
        candidates,
        others,
        hits: searchQuery.data ?? [],
        search,
        wardCode,
        canMoveWard: !!onWardChange,
    })
    const info = m.carrier_legacy_info({
        ward: wardName ?? '',
        names: candidates.map((candidate) => candidate.ward_name).join(', '),
    })
    return (
        <div className="space-y-1">
            <div
                className={cn(
                    'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-sm',
                    confirmed
                        ? 'border-green-600/30 bg-green-50 dark:bg-green-950/30'
                        : 'border-warning/40 bg-warning/5',
                )}
            >
                <label
                    className="font-medium"
                    htmlFor="carrier-legacy-ward"
                    id="carrier-legacy-label"
                >
                    {m.carrier_legacy_label()}
                </label>
                <Combobox
                    id={LEGACY_UNIT_FIELD_ID}
                    ariaLabel={m.carrier_legacy_select_label()}
                    invalid={!!error}
                    className="w-auto min-w-48 flex-1 sm:flex-none"
                    options={options}
                    value={
                        unit.value === undefined
                            ? undefined
                            : String(unit.value)
                    }
                    selectedOption={
                        unit.selected && {
                            value: String(unit.selected.vtp_ward_id),
                            label: unit.selected.ward_name,
                        }
                    }
                    onChange={(value) => {
                        onInteract?.()
                        const move = moveFor(value)
                        if (move) {
                            moveTo(
                                { code: move.ward_code, name: move.ward_name },
                                move.vtp_ward_id,
                            )
                        } else if (value) {
                            unit.choose(Number(value))
                        }
                    }}
                    shouldFilter={false}
                    onSearchChange={setSearch}
                    onOpenChange={(open) => {
                        if (!open) setSearch('')
                    }}
                    searchPlaceholder={m.carrier_legacy_search_placeholder()}
                    emptyText={m.carrier_legacy_search_empty()}
                />
                {unit.selected && <span>, {unit.selected.district_name}</span>}
                <span
                    className={cn(
                        'text-xs',
                        confirmed
                            ? 'text-green-700 dark:text-green-400'
                            : 'text-warning',
                    )}
                >
                    {unit.caption}
                </span>
                {!confirmed && !(addressWard && onWardChange) && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            onInteract?.()
                            unit.confirm()
                        }}
                    >
                        {m.carrier_legacy_confirm()}
                    </Button>
                )}
                <p className="text-muted-foreground basis-full text-xs">
                    {info}
                </p>
            </div>
            {addressWarning}
            {error && (
                <p className="text-destructive text-sm" role="alert">
                    {error}
                </p>
            )}
        </div>
    )
}
